import {ParamType} from './../../../../../@polygonjs/polygonjs/src/engine/poly/ParamType';
import type {MaterialsNetworkSopNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/MaterialsNetwork';
import type {GeoObjNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/obj/Geo';
import type {CameraControlsSopNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/CameraControls';
import type {RootManagerNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/manager/Root';
import type {ExampleData, DataCallbackOptions} from '../../Common';
import {ReturnedNodeDataDict} from '../../ExampleUtils';
import {ActorSopNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/Actor';
import {MeshStandardBuilderMatNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/mat/MeshStandardBuilder';
import {TetSoftBodySolverSopNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/TetSoftBodySolver';
import {SDFBuilderSopNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/SDFBuilder';

export function createData(options: DataCallbackOptions): ExampleData {
	const {scene} = options;
	const root = scene.root();
	const scene_root_nodes: ReturnedNodeDataDict = {};
	function create_geo1(parentNode: RootManagerNode) {
		var geo1 = parentNode.createNode('geo');
		geo1.setName('geo1');
		const geo1_nodes: ReturnedNodeDataDict = {};
		function create_MAT(geo1: GeoObjNode) {
			var MAT = geo1.createNode('materialsNetwork');
			MAT.setName('MAT');
			const MAT_nodes: ReturnedNodeDataDict = {};
			function create_meshPhong1(MAT: MaterialsNetworkSopNode) {
				var meshPhong1 = MAT.createNode('meshPhong');
				meshPhong1.setName('meshPhong1');
				const meshPhong1_nodes: ReturnedNodeDataDict = {};
				meshPhong1.uiData.setPosition(0, 200);
				meshPhong1.p.flatShading.set(true);
				meshPhong1.params.postCreateSpareParams();
				meshPhong1.params.runOnSceneLoadHooks();
				return {node: meshPhong1, children: meshPhong1_nodes};
			}
			MAT_nodes['meshPhong1'] = create_meshPhong1(MAT);
			if (MAT.childrenController) {
				MAT.childrenController.selection.set([]);
			}
			MAT.uiData.setPosition(-400, 350);
			MAT.params.postCreateSpareParams();
			MAT.params.runOnSceneLoadHooks();
			return {node: MAT, children: MAT_nodes};
		}
		function create_tetrahedralize1(geo1: GeoObjNode) {
			var tetrahedralize1 = geo1.createNode('tetrahedralize');
			tetrahedralize1.setName('tetrahedralize1');
			const tetrahedralize1_nodes: ReturnedNodeDataDict = {};
			tetrahedralize1.uiData.setComment(
				'This node converts the input triangles into tetrahedrons, which is the required format to simulate a soft body.\nChanging those settings has a big influence on how the simulation will behave.'
			);
			tetrahedralize1.uiData.setPosition(200, 750);
			tetrahedralize1.p.fuseDist.set(0.09);
			tetrahedralize1.p.minQuality.set(0.12);
			tetrahedralize1.params.postCreateSpareParams();
			tetrahedralize1.params.runOnSceneLoadHooks();
			return {node: tetrahedralize1, children: tetrahedralize1_nodes};
		}
		function create_tetSoftBodySolver1(geo1: GeoObjNode) {
			var tetSoftBodySolver1 = geo1.createNode('tetSoftBodySolver');
			tetSoftBodySolver1.setName('tetSoftBodySolver1');
			const tetSoftBodySolver1_nodes: ReturnedNodeDataDict = {};
			function create_globals1(tetSoftBodySolver1: TetSoftBodySolverSopNode) {
				var globals1 = tetSoftBodySolver1.createNode('globals');
				globals1.setName('globals1');
				const globals1_nodes: ReturnedNodeDataDict = {};
				globals1.uiData.setPosition(-500, 0);
				globals1.params.postCreateSpareParams();
				globals1.params.runOnSceneLoadHooks();
				return {node: globals1, children: globals1_nodes};
			}
			function create_output1(tetSoftBodySolver1: TetSoftBodySolverSopNode) {
				var output1 = tetSoftBodySolver1.createNode('output');
				output1.setName('output1');
				const output1_nodes: ReturnedNodeDataDict = {};
				output1.uiData.setPosition(200, 0);
				output1.params.postCreateSpareParams();
				output1.params.runOnSceneLoadHooks();
				return {node: output1, children: output1_nodes};
			}
			function create_computeVelocity1(tetSoftBodySolver1: TetSoftBodySolverSopNode) {
				var computeVelocity1 = tetSoftBodySolver1.createNode('computeVelocity');
				computeVelocity1.setName('computeVelocity1');
				const computeVelocity1_nodes: ReturnedNodeDataDict = {};
				computeVelocity1.uiData.setComment(
					'This node allows you to tweak forces, velocity and drag, allowing you to specify them with any function you want. You can for instance:\n- have a force that is high in specific areas, and control its vector. \n- set an area with a high drag.\n- animate forces or drag based on time\n- animate forces or drag based user inputs, via a param node'
				);
				computeVelocity1.uiData.setPosition(-100, 0);
				computeVelocity1.params.postCreateSpareParams();
				computeVelocity1.params.runOnSceneLoadHooks();
				return {node: computeVelocity1, children: computeVelocity1_nodes};
			}
			function create_SDFPlane1(tetSoftBodySolver1: TetSoftBodySolverSopNode) {
				var SDFPlane1 = tetSoftBodySolver1.createNode('SDFPlane');
				SDFPlane1.setName('SDFPlane1');
				const SDFPlane1_nodes: ReturnedNodeDataDict = {};
				SDFPlane1.uiData.setPosition(-400, 850);
				SDFPlane1.params.postCreateSpareParams();
				SDFPlane1.params.runOnSceneLoadHooks();
				return {node: SDFPlane1, children: SDFPlane1_nodes};
			}
			function create_constant_GRAVITY(tetSoftBodySolver1: TetSoftBodySolverSopNode) {
				var constant_GRAVITY = tetSoftBodySolver1.createNode('constant');
				constant_GRAVITY.setName('constant_GRAVITY');
				const constant_GRAVITY_nodes: ReturnedNodeDataDict = {};
				constant_GRAVITY.uiData.setPosition(-300, -200);
				constant_GRAVITY.p.type.set(6);
				constant_GRAVITY.p.vector3.set([0, -9.8, 0]);
				constant_GRAVITY.params.postCreateSpareParams();
				constant_GRAVITY.params.runOnSceneLoadHooks();
				return {node: constant_GRAVITY, children: constant_GRAVITY_nodes};
			}
			function create_SDFTube1(tetSoftBodySolver1: TetSoftBodySolverSopNode) {
				var SDFTube1 = tetSoftBodySolver1.createNode('SDFTube');
				SDFTube1.setName('SDFTube1');
				const SDFTube1_nodes: ReturnedNodeDataDict = {};
				SDFTube1.uiData.setPosition(-400, 1000);
				SDFTube1.p.center.set([-3, 0, 0]);
				SDFTube1.params.postCreateSpareParams();
				SDFTube1.params.runOnSceneLoadHooks();
				return {node: SDFTube1, children: SDFTube1_nodes};
			}
			function create_SDFTube2(tetSoftBodySolver1: TetSoftBodySolverSopNode) {
				var SDFTube2 = tetSoftBodySolver1.createNode('SDFTube');
				SDFTube2.setName('SDFTube2');
				const SDFTube2_nodes: ReturnedNodeDataDict = {};
				SDFTube2.uiData.setPosition(-400, 1100);
				SDFTube2.p.center.set([3, 0, 0]);
				SDFTube2.params.postCreateSpareParams();
				SDFTube2.params.runOnSceneLoadHooks();
				return {node: SDFTube2, children: SDFTube2_nodes};
			}
			function create_SDFUnion1(tetSoftBodySolver1: TetSoftBodySolverSopNode) {
				var SDFUnion1 = tetSoftBodySolver1.createNode('SDFUnion');
				SDFUnion1.setName('SDFUnion1');
				const SDFUnion1_nodes: ReturnedNodeDataDict = {};
				SDFUnion1.uiData.setPosition(-250, 1050);
				SDFUnion1.p.smooth.set(false);
				SDFUnion1.params.postCreateSpareParams();
				SDFUnion1.params.runOnSceneLoadHooks();
				return {node: SDFUnion1, children: SDFUnion1_nodes};
			}
			function create_SDFUnion2(tetSoftBodySolver1: TetSoftBodySolverSopNode) {
				var SDFUnion2 = tetSoftBodySolver1.createNode('SDFUnion');
				SDFUnion2.setName('SDFUnion2');
				const SDFUnion2_nodes: ReturnedNodeDataDict = {};
				SDFUnion2.uiData.setPosition(-150, 850);
				SDFUnion2.p.smooth.set(false);
				SDFUnion2.params.postCreateSpareParams();
				SDFUnion2.params.runOnSceneLoadHooks();
				return {node: SDFUnion2, children: SDFUnion2_nodes};
			}
			function create_SDFBox1(tetSoftBodySolver1: TetSoftBodySolverSopNode) {
				var SDFBox1 = tetSoftBodySolver1.createNode('SDFBox');
				SDFBox1.setName('SDFBox1');
				const SDFBox1_nodes: ReturnedNodeDataDict = {};
				SDFBox1.uiData.setPosition(-500, 550);
				SDFBox1.p.sizes.set([10, 10, 10]);
				SDFBox1.params.postCreateSpareParams();
				SDFBox1.params.runOnSceneLoadHooks();
				return {node: SDFBox1, children: SDFBox1_nodes};
			}
			function create_negate1(tetSoftBodySolver1: TetSoftBodySolverSopNode) {
				var negate1 = tetSoftBodySolver1.createNode('negate');
				negate1.setName('negate1');
				const negate1_nodes: ReturnedNodeDataDict = {};
				negate1.uiData.setPosition(-350, 550);
				negate1.addParam(ParamType.FLOAT, 'value', 0, {
					spare: true,
					editable: false,
					computeOnDirty: true,
					dependentOnFoundParam: false,
				});
				negate1.params.postCreateSpareParams();
				negate1.params.runOnSceneLoadHooks();
				return {node: negate1, children: negate1_nodes};
			}
			function create_SDFUnion3(tetSoftBodySolver1: TetSoftBodySolverSopNode) {
				var SDFUnion3 = tetSoftBodySolver1.createNode('SDFUnion');
				SDFUnion3.setName('SDFUnion3');
				const SDFUnion3_nodes: ReturnedNodeDataDict = {};
				SDFUnion3.uiData.setComment(
					'Those nodes defined the collision objects, using SDF functions, which are the same used in the raymarching material, or the [SDFBuilder node](../../SDFBuilder1)'
				);
				SDFUnion3.uiData.setPosition(-50, 600);
				SDFUnion3.p.smooth.set(false);
				SDFUnion3.params.postCreateSpareParams();
				SDFUnion3.params.runOnSceneLoadHooks();
				return {node: SDFUnion3, children: SDFUnion3_nodes};
			}
			tetSoftBodySolver1_nodes['globals1'] = create_globals1(tetSoftBodySolver1);
			tetSoftBodySolver1_nodes['output1'] = create_output1(tetSoftBodySolver1);
			tetSoftBodySolver1_nodes['computeVelocity1'] = create_computeVelocity1(tetSoftBodySolver1);
			tetSoftBodySolver1_nodes['SDFPlane1'] = create_SDFPlane1(tetSoftBodySolver1);
			tetSoftBodySolver1_nodes['constant_GRAVITY'] = create_constant_GRAVITY(tetSoftBodySolver1);
			tetSoftBodySolver1_nodes['SDFTube1'] = create_SDFTube1(tetSoftBodySolver1);
			tetSoftBodySolver1_nodes['SDFTube2'] = create_SDFTube2(tetSoftBodySolver1);
			tetSoftBodySolver1_nodes['SDFUnion1'] = create_SDFUnion1(tetSoftBodySolver1);
			tetSoftBodySolver1_nodes['SDFUnion2'] = create_SDFUnion2(tetSoftBodySolver1);
			tetSoftBodySolver1_nodes['SDFBox1'] = create_SDFBox1(tetSoftBodySolver1);
			tetSoftBodySolver1_nodes['negate1'] = create_negate1(tetSoftBodySolver1);
			tetSoftBodySolver1_nodes['SDFUnion3'] = create_SDFUnion3(tetSoftBodySolver1);
			tetSoftBodySolver1_nodes['output1'].node.setInput(
				'velocity',
				tetSoftBodySolver1_nodes['computeVelocity1'].node,
				'velocity'
			);
			tetSoftBodySolver1_nodes['output1'].node.setInput(
				'collisionSDF',
				tetSoftBodySolver1_nodes['SDFUnion3'].node,
				'union'
			);
			tetSoftBodySolver1_nodes['computeVelocity1'].node.setInput(
				'forces',
				tetSoftBodySolver1_nodes['constant_GRAVITY'].node,
				'val'
			);
			tetSoftBodySolver1_nodes['computeVelocity1'].node.setInput(
				'velocity',
				tetSoftBodySolver1_nodes['globals1'].node,
				'velocity'
			);
			tetSoftBodySolver1_nodes['computeVelocity1'].node.setInput(
				'delta',
				tetSoftBodySolver1_nodes['globals1'].node,
				'delta'
			);
			tetSoftBodySolver1_nodes['SDFUnion1'].node.setInput(
				'sdf0',
				tetSoftBodySolver1_nodes['SDFTube1'].node,
				'float'
			);
			tetSoftBodySolver1_nodes['SDFUnion1'].node.setInput(
				'sdf1',
				tetSoftBodySolver1_nodes['SDFTube2'].node,
				'float'
			);
			tetSoftBodySolver1_nodes['SDFUnion2'].node.setInput(
				'sdf0',
				tetSoftBodySolver1_nodes['SDFPlane1'].node,
				'float'
			);
			tetSoftBodySolver1_nodes['SDFUnion2'].node.setInput(
				'sdf1',
				tetSoftBodySolver1_nodes['SDFUnion1'].node,
				'union'
			);
			tetSoftBodySolver1_nodes['negate1'].node.setInput(
				'value',
				tetSoftBodySolver1_nodes['SDFBox1'].node,
				'float'
			);
			tetSoftBodySolver1_nodes['SDFUnion3'].node.setInput(
				'sdf0',
				tetSoftBodySolver1_nodes['negate1'].node,
				'negate'
			);
			tetSoftBodySolver1_nodes['SDFUnion3'].node.setInput(
				'sdf1',
				tetSoftBodySolver1_nodes['SDFUnion2'].node,
				'union'
			);
			if (tetSoftBodySolver1.childrenController) {
				tetSoftBodySolver1.childrenController.selection.set([]);
			}
			tetSoftBodySolver1.uiData.setComment(
				'This is the main part of the solver. You can [dive inside](.) to tweak the settings and collision objects.'
			);
			tetSoftBodySolver1.uiData.setPosition(200, 1100);
			tetSoftBodySolver1.params.postCreateSpareParams();
			tetSoftBodySolver1.params.runOnSceneLoadHooks();
			return {node: tetSoftBodySolver1, children: tetSoftBodySolver1_nodes};
		}
		function create_actor_softBody1(geo1: GeoObjNode) {
			var actor_softBody1 = geo1.createNode('actor');
			actor_softBody1.setName('actor_softBody1');
			const actor_softBody1_nodes: ReturnedNodeDataDict = {};
			function create_softBodySolverStepSimulation1(actor_softBody1: ActorSopNode) {
				var softBodySolverStepSimulation1 = actor_softBody1.createNode('softBodySolverStepSimulation');
				softBodySolverStepSimulation1.setName('softBodySolverStepSimulation1');
				const softBodySolverStepSimulation1_nodes: ReturnedNodeDataDict = {};
				softBodySolverStepSimulation1.uiData.setPosition(200, 0);
				softBodySolverStepSimulation1.p.edgeCompliance.set(17);
				softBodySolverStepSimulation1.params.postCreateSpareParams();
				softBodySolverStepSimulation1.params.runOnSceneLoadHooks();
				return {node: softBodySolverStepSimulation1, children: softBodySolverStepSimulation1_nodes};
			}
			function create_onTick1(actor_softBody1: ActorSopNode) {
				var onTick1 = actor_softBody1.createNode('onTick');
				onTick1.setName('onTick1');
				const onTick1_nodes: ReturnedNodeDataDict = {};
				onTick1.uiData.setPosition(-100, 0);
				onTick1.params.postCreateSpareParams();
				onTick1.params.runOnSceneLoadHooks();
				return {node: onTick1, children: onTick1_nodes};
			}
			function create_createSoftBodyConstraint1(actor_softBody1: ActorSopNode) {
				var createSoftBodyConstraint1 = actor_softBody1.createNode('createSoftBodyConstraint');
				createSoftBodyConstraint1.setName('createSoftBodyConstraint1');
				const createSoftBodyConstraint1_nodes: ReturnedNodeDataDict = {};
				createSoftBodyConstraint1.uiData.setComment(
					'This creates a constraints, using the `id` on which we clicked.'
				);
				createSoftBodyConstraint1.uiData.setPosition(100, 350);
				createSoftBodyConstraint1.params.postCreateSpareParams();
				createSoftBodyConstraint1.params.runOnSceneLoadHooks();
				return {node: createSoftBodyConstraint1, children: createSoftBodyConstraint1_nodes};
			}
			function create_deleteSoftBodyConstraint1(actor_softBody1: ActorSopNode) {
				var deleteSoftBodyConstraint1 = actor_softBody1.createNode('deleteSoftBodyConstraint');
				deleteSoftBodyConstraint1.setName('deleteSoftBodyConstraint1');
				const deleteSoftBodyConstraint1_nodes: ReturnedNodeDataDict = {};
				deleteSoftBodyConstraint1.uiData.setComment(
					'Here we delete the constraint when the user releases the cursor.'
				);
				deleteSoftBodyConstraint1.uiData.setPosition(400, 1000);
				deleteSoftBodyConstraint1.params.postCreateSpareParams();
				deleteSoftBodyConstraint1.params.runOnSceneLoadHooks();
				return {node: deleteSoftBodyConstraint1, children: deleteSoftBodyConstraint1_nodes};
			}
			function create_setSoftBodyConstraintPosition1(actor_softBody1: ActorSopNode) {
				var setSoftBodyConstraintPosition1 = actor_softBody1.createNode('setSoftBodyConstraintPosition');
				setSoftBodyConstraintPosition1.setName('setSoftBodyConstraintPosition1');
				const setSoftBodyConstraintPosition1_nodes: ReturnedNodeDataDict = {};
				setSoftBodyConstraintPosition1.uiData.setComment(
					'This sets the position of the constraint. The position is defined by the intersection of a ray from the camera and a place.'
				);
				setSoftBodyConstraintPosition1.uiData.setPosition(1350, 350);
				setSoftBodyConstraintPosition1.params.postCreateSpareParams();
				setSoftBodyConstraintPosition1.params.runOnSceneLoadHooks();
				return {node: setSoftBodyConstraintPosition1, children: setSoftBodyConstraintPosition1_nodes};
			}
			function create_onObjectPointerdown1(actor_softBody1: ActorSopNode) {
				var onObjectPointerdown1 = actor_softBody1.createNode('onObjectPointerdown');
				onObjectPointerdown1.setName('onObjectPointerdown1');
				const onObjectPointerdown1_nodes: ReturnedNodeDataDict = {};
				onObjectPointerdown1.uiData.setComment(
					'when we click on the object, we create a constraint used by the cloth solver.\n\nThe constraint is created using the `id` attribute found under the cursor.'
				);
				onObjectPointerdown1.uiData.setPosition(-650, 350);
				onObjectPointerdown1.p.traverseChildren.set(false);
				onObjectPointerdown1.params.postCreateSpareParams();
				onObjectPointerdown1.params.runOnSceneLoadHooks();
				return {node: onObjectPointerdown1, children: onObjectPointerdown1_nodes};
			}
			function create_GetIntersectionAttribute1(actor_softBody1: ActorSopNode) {
				var GetIntersectionAttribute1 = actor_softBody1.createNode('getIntersectionAttribute');
				GetIntersectionAttribute1.setName('GetIntersectionAttribute1');
				const GetIntersectionAttribute1_nodes: ReturnedNodeDataDict = {};
				GetIntersectionAttribute1.uiData.setPosition(-350, 500);
				GetIntersectionAttribute1.p.type.set(3);
				GetIntersectionAttribute1.p.interpolated.set(false);
				GetIntersectionAttribute1.addParam(ParamType.STRING, 'attribName', '', {
					spare: true,
					editable: true,
					computeOnDirty: true,
					dependentOnFoundParam: false,
				});
				GetIntersectionAttribute1.params.get('attribName')!.set('id');
				GetIntersectionAttribute1.addParam(ParamType.INTEGER, 'notFoundValue', -1, {
					spare: true,
					editable: true,
					computeOnDirty: true,
					dependentOnFoundParam: false,
				});
				GetIntersectionAttribute1.params.postCreateSpareParams();
				GetIntersectionAttribute1.params.runOnSceneLoadHooks();
				return {node: GetIntersectionAttribute1, children: GetIntersectionAttribute1_nodes};
			}
			function create_anyTrigger1(actor_softBody1: ActorSopNode) {
				var anyTrigger1 = actor_softBody1.createNode('anyTrigger');
				anyTrigger1.setName('anyTrigger1');
				const anyTrigger1_nodes: ReturnedNodeDataDict = {};
				anyTrigger1.uiData.setPosition(200, 200);
				anyTrigger1.params.postCreateSpareParams();
				anyTrigger1.params.runOnSceneLoadHooks();
				return {node: anyTrigger1, children: anyTrigger1_nodes};
			}
			function create_plane1(actor_softBody1: ActorSopNode) {
				var plane1 = actor_softBody1.createNode('plane');
				plane1.setName('plane1');
				const plane1_nodes: ReturnedNodeDataDict = {};
				plane1.uiData.setPosition(1000, 750);
				plane1.p.constant.set(-0.32);
				plane1.params.postCreateSpareParams();
				plane1.params.runOnSceneLoadHooks();
				return {node: plane1, children: plane1_nodes};
			}
			function create_rayFromCursor1(actor_softBody1: ActorSopNode) {
				var rayFromCursor1 = actor_softBody1.createNode('rayFromCursor');
				rayFromCursor1.setName('rayFromCursor1');
				const rayFromCursor1_nodes: ReturnedNodeDataDict = {};
				rayFromCursor1.uiData.setPosition(1000, 600);
				rayFromCursor1.p.element.set(1);
				rayFromCursor1.params.postCreateSpareParams();
				rayFromCursor1.params.runOnSceneLoadHooks();
				return {node: rayFromCursor1, children: rayFromCursor1_nodes};
			}
			function create_rayIntersectPlane1(actor_softBody1: ActorSopNode) {
				var rayIntersectPlane1 = actor_softBody1.createNode('rayIntersectPlane');
				rayIntersectPlane1.setName('rayIntersectPlane1');
				const rayIntersectPlane1_nodes: ReturnedNodeDataDict = {};
				rayIntersectPlane1.uiData.setPosition(1150, 650);
				rayIntersectPlane1.params.postCreateSpareParams();
				rayIntersectPlane1.params.runOnSceneLoadHooks();
				return {node: rayIntersectPlane1, children: rayIntersectPlane1_nodes};
			}
			function create_onPointerup1(actor_softBody1: ActorSopNode) {
				var onPointerup1 = actor_softBody1.createNode('onPointerup');
				onPointerup1.setName('onPointerup1');
				const onPointerup1_nodes: ReturnedNodeDataDict = {};
				onPointerup1.uiData.setPosition(-100, 1000);
				onPointerup1.params.postCreateSpareParams();
				onPointerup1.params.runOnSceneLoadHooks();
				return {node: onPointerup1, children: onPointerup1_nodes};
			}
			function create_setObjectAttribute1(actor_softBody1: ActorSopNode) {
				var setObjectAttribute1 = actor_softBody1.createNode('setObjectAttribute');
				setObjectAttribute1.setName('setObjectAttribute1');
				const setObjectAttribute1_nodes: ReturnedNodeDataDict = {};
				setObjectAttribute1.uiData.setComment(
					'We store the id of the constraint in an object attribute, so that we can access it when we need to delete the constraint.'
				);
				setObjectAttribute1.uiData.setPosition(450, 500);
				setObjectAttribute1.p.type.set(3);
				setObjectAttribute1.addParam(ParamType.STRING, 'attribName', '', {
					spare: true,
					editable: false,
					computeOnDirty: true,
					dependentOnFoundParam: false,
				});
				setObjectAttribute1.addParam(ParamType.FLOAT, 'lerp', 1, {
					spare: true,
					editable: true,
					computeOnDirty: true,
					dependentOnFoundParam: false,
				});
				setObjectAttribute1.addParam(ParamType.INTEGER, 'val', 0, {
					spare: true,
					editable: false,
					computeOnDirty: true,
					dependentOnFoundParam: false,
				});
				setObjectAttribute1.params.postCreateSpareParams();
				setObjectAttribute1.params.runOnSceneLoadHooks();
				return {node: setObjectAttribute1, children: setObjectAttribute1_nodes};
			}
			function create_constant1(actor_softBody1: ActorSopNode) {
				var constant1 = actor_softBody1.createNode('constant');
				constant1.setName('constant1');
				const constant1_nodes: ReturnedNodeDataDict = {};
				constant1.uiData.setPosition(0, 650);
				constant1.p.type.set(4);
				constant1.p.string.set('constraintId');
				constant1.params.postCreateSpareParams();
				constant1.params.runOnSceneLoadHooks();
				return {node: constant1, children: constant1_nodes};
			}
			function create_getObjectAttribute1(actor_softBody1: ActorSopNode) {
				var getObjectAttribute1 = actor_softBody1.createNode('getObjectAttribute');
				getObjectAttribute1.setName('getObjectAttribute1');
				const getObjectAttribute1_nodes: ReturnedNodeDataDict = {};
				getObjectAttribute1.uiData.setPosition(150, 850);
				getObjectAttribute1.p.type.set(3);
				getObjectAttribute1.addParam(ParamType.STRING, 'attribName', '', {
					spare: true,
					editable: false,
					computeOnDirty: true,
					dependentOnFoundParam: false,
				});
				getObjectAttribute1.params.get('attribName')!.set('getObjectAttribute1');
				getObjectAttribute1.addParam(ParamType.INTEGER, 'defaultInteger', 0, {
					spare: true,
					editable: true,
					computeOnDirty: true,
					dependentOnFoundParam: false,
				});
				getObjectAttribute1.params.postCreateSpareParams();
				getObjectAttribute1.params.runOnSceneLoadHooks();
				return {node: getObjectAttribute1, children: getObjectAttribute1_nodes};
			}
			actor_softBody1_nodes['softBodySolverStepSimulation1'] =
				create_softBodySolverStepSimulation1(actor_softBody1);
			actor_softBody1_nodes['onTick1'] = create_onTick1(actor_softBody1);
			actor_softBody1_nodes['createSoftBodyConstraint1'] = create_createSoftBodyConstraint1(actor_softBody1);
			actor_softBody1_nodes['deleteSoftBodyConstraint1'] = create_deleteSoftBodyConstraint1(actor_softBody1);
			actor_softBody1_nodes['setSoftBodyConstraintPosition1'] =
				create_setSoftBodyConstraintPosition1(actor_softBody1);
			actor_softBody1_nodes['onObjectPointerdown1'] = create_onObjectPointerdown1(actor_softBody1);
			actor_softBody1_nodes['GetIntersectionAttribute1'] = create_GetIntersectionAttribute1(actor_softBody1);
			actor_softBody1_nodes['anyTrigger1'] = create_anyTrigger1(actor_softBody1);
			actor_softBody1_nodes['plane1'] = create_plane1(actor_softBody1);
			actor_softBody1_nodes['rayFromCursor1'] = create_rayFromCursor1(actor_softBody1);
			actor_softBody1_nodes['rayIntersectPlane1'] = create_rayIntersectPlane1(actor_softBody1);
			actor_softBody1_nodes['onPointerup1'] = create_onPointerup1(actor_softBody1);
			actor_softBody1_nodes['setObjectAttribute1'] = create_setObjectAttribute1(actor_softBody1);
			actor_softBody1_nodes['constant1'] = create_constant1(actor_softBody1);
			actor_softBody1_nodes['getObjectAttribute1'] = create_getObjectAttribute1(actor_softBody1);
			actor_softBody1_nodes['softBodySolverStepSimulation1'].node.setInput(
				'trigger',
				actor_softBody1_nodes['onTick1'].node,
				'trigger'
			);
			actor_softBody1_nodes['createSoftBodyConstraint1'].node.setInput(
				'trigger',
				actor_softBody1_nodes['onObjectPointerdown1'].node,
				'trigger'
			);
			actor_softBody1_nodes['createSoftBodyConstraint1'].node.setInput(
				'index',
				actor_softBody1_nodes['GetIntersectionAttribute1'].node,
				'val'
			);
			actor_softBody1_nodes['deleteSoftBodyConstraint1'].node.setInput(
				'trigger',
				actor_softBody1_nodes['onPointerup1'].node,
				'trigger'
			);
			actor_softBody1_nodes['deleteSoftBodyConstraint1'].node.setInput(
				'id',
				actor_softBody1_nodes['getObjectAttribute1'].node,
				'val'
			);
			actor_softBody1_nodes['setSoftBodyConstraintPosition1'].node.setInput(
				'trigger',
				actor_softBody1_nodes['anyTrigger1'].node,
				'trigger'
			);
			actor_softBody1_nodes['setSoftBodyConstraintPosition1'].node.setInput(
				'id',
				actor_softBody1_nodes['createSoftBodyConstraint1'].node,
				'id'
			);
			actor_softBody1_nodes['setSoftBodyConstraintPosition1'].node.setInput(
				'position',
				actor_softBody1_nodes['rayIntersectPlane1'].node,
				'position'
			);
			actor_softBody1_nodes['GetIntersectionAttribute1'].node.setInput(
				'Intersection',
				actor_softBody1_nodes['onObjectPointerdown1'].node,
				'Intersection'
			);
			actor_softBody1_nodes['anyTrigger1'].node.setInput(
				'trigger0',
				actor_softBody1_nodes['onTick1'].node,
				'trigger'
			);
			actor_softBody1_nodes['anyTrigger1'].node.setInput(
				'trigger1',
				actor_softBody1_nodes['createSoftBodyConstraint1'].node,
				'trigger'
			);
			actor_softBody1_nodes['rayIntersectPlane1'].node.setInput(
				'Ray',
				actor_softBody1_nodes['rayFromCursor1'].node,
				'Ray'
			);
			actor_softBody1_nodes['rayIntersectPlane1'].node.setInput(
				'Plane',
				actor_softBody1_nodes['plane1'].node,
				'Plane'
			);
			actor_softBody1_nodes['setObjectAttribute1'].node.setInput(
				'trigger',
				actor_softBody1_nodes['createSoftBodyConstraint1'].node,
				'trigger'
			);
			actor_softBody1_nodes['setObjectAttribute1'].node.setInput(
				'attribName',
				actor_softBody1_nodes['constant1'].node,
				'val'
			);
			actor_softBody1_nodes['setObjectAttribute1'].node.setInput(
				'val',
				actor_softBody1_nodes['createSoftBodyConstraint1'].node,
				'id'
			);
			actor_softBody1_nodes['getObjectAttribute1'].node.setInput(
				'attribName',
				actor_softBody1_nodes['constant1'].node,
				'val'
			);
			if (actor_softBody1.childrenController) {
				actor_softBody1.childrenController.selection.set([]);
			}
			actor_softBody1.uiData.setComment(
				'[Dive inside](.) this node to see how it is possible to grab the soft body, drag it around, and release it.'
			);
			actor_softBody1.uiData.setPosition(200, 1300);
			actor_softBody1.params.postCreateSpareParams();
			actor_softBody1.params.runOnSceneLoadHooks();
			return {node: actor_softBody1, children: actor_softBody1_nodes};
		}
		function create_hierarchy3(geo1: GeoObjNode) {
			var hierarchy3 = geo1.createNode('hierarchy');
			hierarchy3.setName('hierarchy3');
			const hierarchy3_nodes: ReturnedNodeDataDict = {};
			hierarchy3.uiData.setPosition(200, 100);
			hierarchy3.p.mode.set(1);
			hierarchy3.params.postCreateSpareParams();
			hierarchy3.params.runOnSceneLoadHooks();
			return {node: hierarchy3, children: hierarchy3_nodes};
		}
		function create_fuse3(geo1: GeoObjNode) {
			var fuse3 = geo1.createNode('fuse');
			fuse3.setName('fuse3');
			const fuse3_nodes: ReturnedNodeDataDict = {};
			fuse3.uiData.setComment(
				'This node and the ones above are simply importing and preparing the geometry. This fuse node also helps reducing the polygons count.'
			);
			fuse3.uiData.setPosition(200, 500);
			fuse3.p.dist.set(0.02);
			fuse3.params.postCreateSpareParams();
			fuse3.params.runOnSceneLoadHooks();
			return {node: fuse3, children: fuse3_nodes};
		}
		function create_transform6(geo1: GeoObjNode) {
			var transform6 = geo1.createNode('transform');
			transform6.setName('transform6');
			const transform6_nodes: ReturnedNodeDataDict = {};
			transform6.uiData.setPosition(200, 350);
			transform6.p.t.set([0, 0.8825375619663385, 0]);
			transform6.params.postCreateSpareParams();
			transform6.params.runOnSceneLoadHooks();
			return {node: transform6, children: transform6_nodes};
		}
		function create_fileGLTF3(geo1: GeoObjNode) {
			var fileGLTF3 = geo1.createNode('fileGLTF');
			fileGLTF3.setName('fileGLTF3');
			const fileGLTF3_nodes: ReturnedNodeDataDict = {};
			fileGLTF3.uiData.setPosition(200, -50);
			fileGLTF3.p.url.set(
				'https://raw.githubusercontent.com/polygonjs/polygonjs-assets/master/models/resources/threedscans.com/einstein.glb'
			);
			fileGLTF3.params.postCreateSpareParams();
			fileGLTF3.params.runOnSceneLoadHooks();
			return {node: fileGLTF3, children: fileGLTF3_nodes};
		}
		function create_objectProperties1(geo1: GeoObjNode) {
			var objectProperties1 = geo1.createNode('objectProperties');
			objectProperties1.setName('objectProperties1');
			const objectProperties1_nodes: ReturnedNodeDataDict = {};
			objectProperties1.uiData.setPosition(200, 1500);
			objectProperties1.p.tcastShadow.set(true);
			objectProperties1.p.treceiveShadow.set(true);
			objectProperties1.params.postCreateSpareParams();
			objectProperties1.params.runOnSceneLoadHooks();
			return {node: objectProperties1, children: objectProperties1_nodes};
		}
		function create_SDFBuilder1(geo1: GeoObjNode) {
			var SDFBuilder1 = geo1.createNode('SDFBuilder');
			SDFBuilder1.setName('SDFBuilder1');
			const SDFBuilder1_nodes: ReturnedNodeDataDict = {};
			function create_globals1(SDFBuilder1: SDFBuilderSopNode) {
				var globals1 = SDFBuilder1.createNode('globals');
				globals1.setName('globals1');
				const globals1_nodes: ReturnedNodeDataDict = {};
				globals1.uiData.setPosition(-550, 0);
				globals1.params.postCreateSpareParams();
				globals1.params.runOnSceneLoadHooks();
				return {node: globals1, children: globals1_nodes};
			}
			function create_output1(SDFBuilder1: SDFBuilderSopNode) {
				var output1 = SDFBuilder1.createNode('output');
				output1.setName('output1');
				const output1_nodes: ReturnedNodeDataDict = {};
				output1.uiData.setPosition(0, 0);
				output1.params.postCreateSpareParams();
				output1.params.runOnSceneLoadHooks();
				return {node: output1, children: output1_nodes};
			}
			function create_SDFTube1(SDFBuilder1: SDFBuilderSopNode) {
				var SDFTube1 = SDFBuilder1.createNode('SDFTube');
				SDFTube1.setName('SDFTube1');
				const SDFTube1_nodes: ReturnedNodeDataDict = {};
				SDFTube1.uiData.setPosition(-200, 0);
				SDFTube1.p.center.set([3, 0, 0]);
				SDFTube1.params.postCreateSpareParams();
				SDFTube1.params.runOnSceneLoadHooks();
				return {node: SDFTube1, children: SDFTube1_nodes};
			}
			SDFBuilder1_nodes['globals1'] = create_globals1(SDFBuilder1);
			SDFBuilder1_nodes['output1'] = create_output1(SDFBuilder1);
			SDFBuilder1_nodes['SDFTube1'] = create_SDFTube1(SDFBuilder1);
			SDFBuilder1_nodes['output1'].node.setInput('d', SDFBuilder1_nodes['SDFTube1'].node, 'float');
			SDFBuilder1_nodes['SDFTube1'].node.setInput('position', SDFBuilder1_nodes['globals1'].node, 'position');
			if (SDFBuilder1.childrenController) {
				SDFBuilder1.childrenController.selection.set([]);
			}
			SDFBuilder1.uiData.setPosition(1000, 1150);
			SDFBuilder1.params.postCreateSpareParams();
			SDFBuilder1.params.runOnSceneLoadHooks();
			return {node: SDFBuilder1, children: SDFBuilder1_nodes};
		}
		function create_boxLines1(geo1: GeoObjNode) {
			var boxLines1 = geo1.createNode('boxLines');
			boxLines1.setName('boxLines1');
			const boxLines1_nodes: ReturnedNodeDataDict = {};
			boxLines1.uiData.setPosition(1000, 900);
			boxLines1.p.sizes.set([1, 2, 1]);
			boxLines1.p.center.set([3, 0, 0]);
			boxLines1.params.postCreateSpareParams();
			boxLines1.params.runOnSceneLoadHooks();
			return {node: boxLines1, children: boxLines1_nodes};
		}
		function create_SDFBuilder2(geo1: GeoObjNode) {
			var SDFBuilder2 = geo1.createNode('SDFBuilder');
			SDFBuilder2.setName('SDFBuilder2');
			const SDFBuilder2_nodes: ReturnedNodeDataDict = {};
			function create_globals1(SDFBuilder2: SDFBuilderSopNode) {
				var globals1 = SDFBuilder2.createNode('globals');
				globals1.setName('globals1');
				const globals1_nodes: ReturnedNodeDataDict = {};
				globals1.uiData.setPosition(-550, 0);
				globals1.params.postCreateSpareParams();
				globals1.params.runOnSceneLoadHooks();
				return {node: globals1, children: globals1_nodes};
			}
			function create_output1(SDFBuilder2: SDFBuilderSopNode) {
				var output1 = SDFBuilder2.createNode('output');
				output1.setName('output1');
				const output1_nodes: ReturnedNodeDataDict = {};
				output1.uiData.setPosition(0, 0);
				output1.params.postCreateSpareParams();
				output1.params.runOnSceneLoadHooks();
				return {node: output1, children: output1_nodes};
			}
			function create_SDFTube1(SDFBuilder2: SDFBuilderSopNode) {
				var SDFTube1 = SDFBuilder2.createNode('SDFTube');
				SDFTube1.setName('SDFTube1');
				const SDFTube1_nodes: ReturnedNodeDataDict = {};
				SDFTube1.uiData.setPosition(-200, 0);
				SDFTube1.p.center.set([-3, 0, 0]);
				SDFTube1.params.postCreateSpareParams();
				SDFTube1.params.runOnSceneLoadHooks();
				return {node: SDFTube1, children: SDFTube1_nodes};
			}
			SDFBuilder2_nodes['globals1'] = create_globals1(SDFBuilder2);
			SDFBuilder2_nodes['output1'] = create_output1(SDFBuilder2);
			SDFBuilder2_nodes['SDFTube1'] = create_SDFTube1(SDFBuilder2);
			SDFBuilder2_nodes['output1'].node.setInput('d', SDFBuilder2_nodes['SDFTube1'].node, 'float');
			SDFBuilder2_nodes['SDFTube1'].node.setInput('position', SDFBuilder2_nodes['globals1'].node, 'position');
			if (SDFBuilder2.childrenController) {
				SDFBuilder2.childrenController.selection.set([SDFBuilder2_nodes['SDFTube1'].node]);
			}
			SDFBuilder2.uiData.setPosition(700, 1150);
			SDFBuilder2.params.postCreateSpareParams();
			SDFBuilder2.params.runOnSceneLoadHooks();
			return {node: SDFBuilder2, children: SDFBuilder2_nodes};
		}
		function create_boxLines2(geo1: GeoObjNode) {
			var boxLines2 = geo1.createNode('boxLines');
			boxLines2.setName('boxLines2');
			const boxLines2_nodes: ReturnedNodeDataDict = {};
			boxLines2.uiData.setPosition(700, 900);
			boxLines2.p.sizes.set([1, 2, 1]);
			boxLines2.p.center.set([-3, 0, 0]);
			boxLines2.params.postCreateSpareParams();
			boxLines2.params.runOnSceneLoadHooks();
			return {node: boxLines2, children: boxLines2_nodes};
		}
		function create_merge1(geo1: GeoObjNode) {
			var merge1 = geo1.createNode('merge');
			merge1.setName('merge1');
			const merge1_nodes: ReturnedNodeDataDict = {};
			merge1.uiData.setComment(
				'Those SDFBuilder nodes display the [collision object](../tetSoftBodySolver1) used by the solver. But be mindful that they are replicating them, and are not tied to them yet. So changing one does not change the other, they currently have to be synced manually, or using parameters and expressions.'
			);
			merge1.uiData.setPosition(850, 1350);
			merge1.params.postCreateSpareParams();
			merge1.params.runOnSceneLoadHooks();
			return {node: merge1, children: merge1_nodes};
		}
		function create_merge2(geo1: GeoObjNode) {
			var merge2 = geo1.createNode('merge');
			merge2.setName('merge2');
			const merge2_nodes: ReturnedNodeDataDict = {};
			merge2.uiData.setPosition(400, 1950);
			merge2.flags.display.set(true);
			merge2.params.postCreateSpareParams();
			merge2.params.runOnSceneLoadHooks();
			return {node: merge2, children: merge2_nodes};
		}
		function create_material1(geo1: GeoObjNode) {
			var material1 = geo1.createNode('material');
			material1.setName('material1');
			const material1_nodes: ReturnedNodeDataDict = {};
			material1.uiData.setComment(
				'here we assign a material with flat shading, which looks a little nicer on geometries converted from tetrahedrons, as they have few polygons.'
			);
			material1.uiData.setPosition(200, 1650);
			material1.p.material.set('../MAT/meshPhong1');
			material1.params.postCreateSpareParams();
			material1.params.runOnSceneLoadHooks();
			return {node: material1, children: material1_nodes};
		}
		geo1_nodes['MAT'] = create_MAT(geo1);
		geo1_nodes['tetrahedralize1'] = create_tetrahedralize1(geo1);
		geo1_nodes['tetSoftBodySolver1'] = create_tetSoftBodySolver1(geo1);
		geo1_nodes['actor_softBody1'] = create_actor_softBody1(geo1);
		geo1_nodes['hierarchy3'] = create_hierarchy3(geo1);
		geo1_nodes['fuse3'] = create_fuse3(geo1);
		geo1_nodes['transform6'] = create_transform6(geo1);
		geo1_nodes['fileGLTF3'] = create_fileGLTF3(geo1);
		geo1_nodes['objectProperties1'] = create_objectProperties1(geo1);
		geo1_nodes['SDFBuilder1'] = create_SDFBuilder1(geo1);
		geo1_nodes['boxLines1'] = create_boxLines1(geo1);
		geo1_nodes['SDFBuilder2'] = create_SDFBuilder2(geo1);
		geo1_nodes['boxLines2'] = create_boxLines2(geo1);
		geo1_nodes['merge1'] = create_merge1(geo1);
		geo1_nodes['merge2'] = create_merge2(geo1);
		geo1_nodes['material1'] = create_material1(geo1);
		geo1_nodes['tetrahedralize1'].node.setInput(0, geo1_nodes['fuse3'].node);
		geo1_nodes['tetSoftBodySolver1'].node.setInput(0, geo1_nodes['tetrahedralize1'].node);
		geo1_nodes['actor_softBody1'].node.setInput(0, geo1_nodes['tetSoftBodySolver1'].node);
		geo1_nodes['hierarchy3'].node.setInput(0, geo1_nodes['fileGLTF3'].node);
		geo1_nodes['fuse3'].node.setInput(0, geo1_nodes['transform6'].node);
		geo1_nodes['transform6'].node.setInput(0, geo1_nodes['hierarchy3'].node);
		geo1_nodes['objectProperties1'].node.setInput(0, geo1_nodes['actor_softBody1'].node);
		geo1_nodes['SDFBuilder1'].node.setInput(0, geo1_nodes['boxLines1'].node);
		geo1_nodes['SDFBuilder2'].node.setInput(0, geo1_nodes['boxLines2'].node);
		geo1_nodes['merge1'].node.setInput(0, geo1_nodes['SDFBuilder2'].node);
		geo1_nodes['merge1'].node.setInput(1, geo1_nodes['SDFBuilder1'].node);
		geo1_nodes['merge2'].node.setInput(0, geo1_nodes['material1'].node);
		geo1_nodes['merge2'].node.setInput(1, geo1_nodes['merge1'].node);
		geo1_nodes['material1'].node.setInput(0, geo1_nodes['objectProperties1'].node);
		if (geo1.childrenController) {
			geo1.childrenController.selection.set([]);
		}
		geo1.uiData.setPosition(-50, -300);
		geo1.flags.display.set(true);
		geo1.params.postCreateSpareParams();
		geo1.params.runOnSceneLoadHooks();
		return {node: geo1, children: geo1_nodes};
	}
	function create_ground(parentNode: RootManagerNode) {
		var ground = parentNode.createNode('geo');
		ground.setName('ground');
		const ground_nodes: ReturnedNodeDataDict = {};
		function create_material1(ground: GeoObjNode) {
			var material1 = ground.createNode('material');
			material1.setName('material1');
			const material1_nodes: ReturnedNodeDataDict = {};
			material1.uiData.setPosition(0, 300);
			material1.flags.display.set(true);
			material1.p.material.set('../MAT/meshStandardBuilder1');
			material1.params.postCreateSpareParams();
			material1.params.runOnSceneLoadHooks();
			return {node: material1, children: material1_nodes};
		}
		function create_MAT(ground: GeoObjNode) {
			var MAT = ground.createNode('materialsNetwork');
			MAT.setName('MAT');
			const MAT_nodes: ReturnedNodeDataDict = {};
			function create_meshStandardBuilder1(MAT: MaterialsNetworkSopNode) {
				var meshStandardBuilder1 = MAT.createNode('meshStandardBuilder');
				meshStandardBuilder1.setName('meshStandardBuilder1');
				const meshStandardBuilder1_nodes: ReturnedNodeDataDict = {};
				function create_globals1(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var globals1 = meshStandardBuilder1.createNode('globals');
					globals1.setName('globals1');
					const globals1_nodes: ReturnedNodeDataDict = {};
					globals1.uiData.setPosition(-450, 0);
					globals1.params.postCreateSpareParams();
					globals1.params.runOnSceneLoadHooks();
					return {node: globals1, children: globals1_nodes};
				}
				function create_output1(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var output1 = meshStandardBuilder1.createNode('output');
					output1.setName('output1');
					const output1_nodes: ReturnedNodeDataDict = {};
					output1.uiData.setPosition(400, 0);
					output1.params.postCreateSpareParams();
					output1.params.runOnSceneLoadHooks();
					return {node: output1, children: output1_nodes};
				}
				function create_vec3ToFloat1(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var vec3ToFloat1 = meshStandardBuilder1.createNode('vec3ToFloat');
					vec3ToFloat1.setName('vec3ToFloat1');
					const vec3ToFloat1_nodes: ReturnedNodeDataDict = {};
					vec3ToFloat1.uiData.setPosition(-200, 200);
					vec3ToFloat1.params.postCreateSpareParams();
					vec3ToFloat1.params.runOnSceneLoadHooks();
					return {node: vec3ToFloat1, children: vec3ToFloat1_nodes};
				}
				function create_floatToVec2_1(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var floatToVec2_1 = meshStandardBuilder1.createNode('floatToVec2');
					floatToVec2_1.setName('floatToVec2_1');
					const floatToVec2_1_nodes: ReturnedNodeDataDict = {};
					floatToVec2_1.uiData.setPosition(-100, 200);
					floatToVec2_1.params.postCreateSpareParams();
					floatToVec2_1.params.runOnSceneLoadHooks();
					return {node: floatToVec2_1, children: floatToVec2_1_nodes};
				}
				function create_checkers1(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var checkers1 = meshStandardBuilder1.createNode('checkers');
					checkers1.setName('checkers1');
					const checkers1_nodes: ReturnedNodeDataDict = {};
					checkers1.uiData.setPosition(50, 200);
					checkers1.params.postCreateSpareParams();
					checkers1.params.runOnSceneLoadHooks();
					return {node: checkers1, children: checkers1_nodes};
				}
				function create_mix1(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var mix1 = meshStandardBuilder1.createNode('mix');
					mix1.setName('mix1');
					const mix1_nodes: ReturnedNodeDataDict = {};
					mix1.uiData.setPosition(250, 0);
					mix1.addParam(ParamType.VECTOR3, 'value0', [0, 0, 0], {
						spare: true,
						editable: false,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					mix1.addParam(ParamType.VECTOR3, 'value1', [0, 0, 0], {
						spare: true,
						editable: false,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					mix1.addParam(ParamType.FLOAT, 'blend', 0.5, {
						spare: true,
						editable: false,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					mix1.params.postCreateSpareParams();
					mix1.params.runOnSceneLoadHooks();
					return {node: mix1, children: mix1_nodes};
				}
				function create_constant1(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var constant1 = meshStandardBuilder1.createNode('constant');
					constant1.setName('constant1');
					const constant1_nodes: ReturnedNodeDataDict = {};
					constant1.uiData.setPosition(50, -150);
					constant1.p.type.set(4);
					constant1.p.color.set([0.24313725490196078, 0.5098039215686274, 0.8549019607843137]);
					constant1.p.asColor.set(true);
					constant1.params.postCreateSpareParams();
					constant1.params.runOnSceneLoadHooks();
					return {node: constant1, children: constant1_nodes};
				}
				function create_constant2(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var constant2 = meshStandardBuilder1.createNode('constant');
					constant2.setName('constant2');
					const constant2_nodes: ReturnedNodeDataDict = {};
					constant2.uiData.setPosition(0, 0);
					constant2.p.type.set(4);
					constant2.p.color.set([0.047058823529411764, 0.10196078431372549, 0.17647058823529413]);
					constant2.p.asColor.set(true);
					constant2.params.postCreateSpareParams();
					constant2.params.runOnSceneLoadHooks();
					return {node: constant2, children: constant2_nodes};
				}
				meshStandardBuilder1_nodes['globals1'] = create_globals1(meshStandardBuilder1);
				meshStandardBuilder1_nodes['output1'] = create_output1(meshStandardBuilder1);
				meshStandardBuilder1_nodes['vec3ToFloat1'] = create_vec3ToFloat1(meshStandardBuilder1);
				meshStandardBuilder1_nodes['floatToVec2_1'] = create_floatToVec2_1(meshStandardBuilder1);
				meshStandardBuilder1_nodes['checkers1'] = create_checkers1(meshStandardBuilder1);
				meshStandardBuilder1_nodes['mix1'] = create_mix1(meshStandardBuilder1);
				meshStandardBuilder1_nodes['constant1'] = create_constant1(meshStandardBuilder1);
				meshStandardBuilder1_nodes['constant2'] = create_constant2(meshStandardBuilder1);
				meshStandardBuilder1_nodes['output1'].node.setInput(
					'color',
					meshStandardBuilder1_nodes['mix1'].node,
					'mix'
				);
				meshStandardBuilder1_nodes['vec3ToFloat1'].node.setInput(
					'vec',
					meshStandardBuilder1_nodes['globals1'].node,
					'position'
				);
				meshStandardBuilder1_nodes['floatToVec2_1'].node.setInput(
					'x',
					meshStandardBuilder1_nodes['vec3ToFloat1'].node,
					'x'
				);
				meshStandardBuilder1_nodes['floatToVec2_1'].node.setInput(
					'y',
					meshStandardBuilder1_nodes['vec3ToFloat1'].node,
					'z'
				);
				meshStandardBuilder1_nodes['checkers1'].node.setInput(
					'uv',
					meshStandardBuilder1_nodes['floatToVec2_1'].node,
					'vec2'
				);
				meshStandardBuilder1_nodes['mix1'].node.setInput(
					'value0',
					meshStandardBuilder1_nodes['constant1'].node,
					'val'
				);
				meshStandardBuilder1_nodes['mix1'].node.setInput(
					'value1',
					meshStandardBuilder1_nodes['constant2'].node,
					'val'
				);
				meshStandardBuilder1_nodes['mix1'].node.setInput(
					'blend',
					meshStandardBuilder1_nodes['checkers1'].node,
					'checker'
				);
				if (meshStandardBuilder1.childrenController) {
					meshStandardBuilder1.childrenController.selection.set([]);
				}
				meshStandardBuilder1.uiData.setPosition(0, 200);
				meshStandardBuilder1.params.postCreateSpareParams();
				meshStandardBuilder1.params.runOnSceneLoadHooks();
				return {node: meshStandardBuilder1, children: meshStandardBuilder1_nodes};
			}
			MAT_nodes['meshStandardBuilder1'] = create_meshStandardBuilder1(MAT);
			if (MAT.childrenController) {
				MAT.childrenController.selection.set([]);
			}
			MAT.uiData.setPosition(-200, 300);
			MAT.params.postCreateSpareParams();
			MAT.params.runOnSceneLoadHooks();
			return {node: MAT, children: MAT_nodes};
		}
		function create_box1(ground: GeoObjNode) {
			var box1 = ground.createNode('box');
			box1.setName('box1');
			const box1_nodes: ReturnedNodeDataDict = {};
			box1.uiData.setPosition(0, 100);
			box1.p.sizes.set([9.98, 1, 9.98]);
			box1.p.center.set([0, -0.5, 0]);
			box1.params.postCreateSpareParams();
			box1.params.runOnSceneLoadHooks();
			return {node: box1, children: box1_nodes};
		}
		ground_nodes['material1'] = create_material1(ground);
		ground_nodes['MAT'] = create_MAT(ground);
		ground_nodes['box1'] = create_box1(ground);
		ground_nodes['material1'].node.setInput(0, ground_nodes['box1'].node);
		if (ground.childrenController) {
			ground.childrenController.selection.set([]);
		}
		ground.uiData.setPosition(-50, -400);
		ground.flags.display.set(true);
		ground.params.postCreateSpareParams();
		ground.params.runOnSceneLoadHooks();
		return {node: ground, children: ground_nodes};
	}
	function create_lights(parentNode: RootManagerNode) {
		var lights = parentNode.createNode('geo');
		lights.setName('lights');
		const lights_nodes: ReturnedNodeDataDict = {};
		function create_hemisphereLight1(lights: GeoObjNode) {
			var hemisphereLight1 = lights.createNode('hemisphereLight');
			hemisphereLight1.setName('hemisphereLight1');
			const hemisphereLight1_nodes: ReturnedNodeDataDict = {};
			hemisphereLight1.uiData.setPosition(50, -50);
			hemisphereLight1.p.intensity.set(0.52);
			hemisphereLight1.params.postCreateSpareParams();
			hemisphereLight1.params.runOnSceneLoadHooks();
			return {node: hemisphereLight1, children: hemisphereLight1_nodes};
		}
		function create_spotLight1(lights: GeoObjNode) {
			var spotLight1 = lights.createNode('spotLight');
			spotLight1.setName('spotLight1');
			const spotLight1_nodes: ReturnedNodeDataDict = {};
			spotLight1.uiData.setPosition(300, -50);
			spotLight1.p.decay.set(0.1);
			spotLight1.p.distance.set(10);
			spotLight1.p.castShadow.set(true);
			spotLight1.params.postCreateSpareParams();
			spotLight1.params.runOnSceneLoadHooks();
			return {node: spotLight1, children: spotLight1_nodes};
		}
		function create_polarTransform1(lights: GeoObjNode) {
			var polarTransform1 = lights.createNode('polarTransform');
			polarTransform1.setName('polarTransform1');
			const polarTransform1_nodes: ReturnedNodeDataDict = {};
			polarTransform1.uiData.setPosition(300, 150);
			polarTransform1.p.center.set([0, 0.7, 0]);
			polarTransform1.p.longitude.set(79.2);
			polarTransform1.p.latitude.set(7.2);
			polarTransform1.p.depth.set(4.6);
			polarTransform1.params.postCreateSpareParams();
			polarTransform1.params.runOnSceneLoadHooks();
			return {node: polarTransform1, children: polarTransform1_nodes};
		}
		function create_merge1(lights: GeoObjNode) {
			var merge1 = lights.createNode('merge');
			merge1.setName('merge1');
			const merge1_nodes: ReturnedNodeDataDict = {};
			merge1.uiData.setPosition(100, 300);
			merge1.flags.display.set(true);
			merge1.params.postCreateSpareParams();
			merge1.params.runOnSceneLoadHooks();
			return {node: merge1, children: merge1_nodes};
		}
		lights_nodes['hemisphereLight1'] = create_hemisphereLight1(lights);
		lights_nodes['spotLight1'] = create_spotLight1(lights);
		lights_nodes['polarTransform1'] = create_polarTransform1(lights);
		lights_nodes['merge1'] = create_merge1(lights);
		lights_nodes['polarTransform1'].node.setInput(0, lights_nodes['spotLight1'].node);
		lights_nodes['merge1'].node.setInput(0, lights_nodes['hemisphereLight1'].node);
		lights_nodes['merge1'].node.setInput(1, lights_nodes['polarTransform1'].node);
		if (lights.childrenController) {
			lights.childrenController.selection.set([]);
		}
		lights.uiData.setPosition(-50, -200);
		lights.flags.display.set(true);
		lights.params.postCreateSpareParams();
		lights.params.runOnSceneLoadHooks();
		return {node: lights, children: lights_nodes};
	}
	function create_cameras(parentNode: RootManagerNode) {
		var cameras = parentNode.createNode('geo');
		cameras.setName('cameras');
		const cameras_nodes: ReturnedNodeDataDict = {};
		function create_cameraControls1(cameras: GeoObjNode) {
			var cameraControls1 = cameras.createNode('cameraControls');
			cameraControls1.setName('cameraControls1');
			const cameraControls1_nodes: ReturnedNodeDataDict = {};
			function create_cameraOrbitControls1(cameraControls1: CameraControlsSopNode) {
				var cameraOrbitControls1 = cameraControls1.createNode('cameraOrbitControls');
				cameraOrbitControls1.setName('cameraOrbitControls1');
				const cameraOrbitControls1_nodes: ReturnedNodeDataDict = {};
				cameraOrbitControls1.uiData.setPosition(0, 0);
				cameraOrbitControls1.p.target.set([-0.8136739355548704, 0.689643950537663, -0.3221430781354838]);
				cameraOrbitControls1.params.postCreateSpareParams();
				cameraOrbitControls1.params.runOnSceneLoadHooks();
				return {node: cameraOrbitControls1, children: cameraOrbitControls1_nodes};
			}
			cameraControls1_nodes['cameraOrbitControls1'] = create_cameraOrbitControls1(cameraControls1);
			if (cameraControls1.childrenController) {
				cameraControls1.childrenController.selection.set([]);
			}
			cameraControls1.uiData.setPosition(0, 150);
			cameraControls1.p.node.set('cameraOrbitControls1');
			cameraControls1.params.postCreateSpareParams();
			cameraControls1.params.runOnSceneLoadHooks();
			return {node: cameraControls1, children: cameraControls1_nodes};
		}
		function create_perspectiveCamera_DEBUG(cameras: GeoObjNode) {
			var perspectiveCamera_DEBUG = cameras.createNode('perspectiveCamera');
			perspectiveCamera_DEBUG.setName('perspectiveCamera_DEBUG');
			const perspectiveCamera_DEBUG_nodes: ReturnedNodeDataDict = {};
			perspectiveCamera_DEBUG.uiData.setPosition(0, -50);
			perspectiveCamera_DEBUG.p.position.set([1.608706467168788, 1.554179582392217, 5.848803064387538]);
			perspectiveCamera_DEBUG.p.rotation.set([-18.473177623517042, 19.732503241606356, 6.435389783988498]);
			perspectiveCamera_DEBUG.params.postCreateSpareParams();
			perspectiveCamera_DEBUG.params.runOnSceneLoadHooks();
			return {node: perspectiveCamera_DEBUG, children: perspectiveCamera_DEBUG_nodes};
		}
		function create_perspectiveCamera_MAIN(cameras: GeoObjNode) {
			var perspectiveCamera_MAIN = cameras.createNode('perspectiveCamera');
			perspectiveCamera_MAIN.setName('perspectiveCamera_MAIN');
			const perspectiveCamera_MAIN_nodes: ReturnedNodeDataDict = {};
			perspectiveCamera_MAIN.uiData.setPosition(350, -50);
			perspectiveCamera_MAIN.p.position.set([3.3, 3.3, 3.3]);
			perspectiveCamera_MAIN.params.postCreateSpareParams();
			perspectiveCamera_MAIN.params.runOnSceneLoadHooks();
			return {node: perspectiveCamera_MAIN, children: perspectiveCamera_MAIN_nodes};
		}
		function create_polarTransform1(cameras: GeoObjNode) {
			var polarTransform1 = cameras.createNode('polarTransform');
			polarTransform1.setName('polarTransform1');
			const polarTransform1_nodes: ReturnedNodeDataDict = {};
			polarTransform1.uiData.setPosition(350, 150);
			polarTransform1.p.latitude.set(32.4);
			polarTransform1.p.depth.set(5.7);
			polarTransform1.params.postCreateSpareParams();
			polarTransform1.params.runOnSceneLoadHooks();
			return {node: polarTransform1, children: polarTransform1_nodes};
		}
		function create_merge1(cameras: GeoObjNode) {
			var merge1 = cameras.createNode('merge');
			merge1.setName('merge1');
			const merge1_nodes: ReturnedNodeDataDict = {};
			merge1.uiData.setPosition(150, 350);
			merge1.flags.display.set(true);
			merge1.params.postCreateSpareParams();
			merge1.params.runOnSceneLoadHooks();
			return {node: merge1, children: merge1_nodes};
		}
		cameras_nodes['cameraControls1'] = create_cameraControls1(cameras);
		cameras_nodes['perspectiveCamera_DEBUG'] = create_perspectiveCamera_DEBUG(cameras);
		cameras_nodes['perspectiveCamera_MAIN'] = create_perspectiveCamera_MAIN(cameras);
		cameras_nodes['polarTransform1'] = create_polarTransform1(cameras);
		cameras_nodes['merge1'] = create_merge1(cameras);
		cameras_nodes['cameraControls1'].node.setInput(0, cameras_nodes['perspectiveCamera_DEBUG'].node);
		cameras_nodes['polarTransform1'].node.setInput(0, cameras_nodes['perspectiveCamera_MAIN'].node);
		cameras_nodes['merge1'].node.setInput(0, cameras_nodes['cameraControls1'].node);
		cameras_nodes['merge1'].node.setInput(1, cameras_nodes['polarTransform1'].node);
		if (cameras.childrenController) {
			cameras.childrenController.selection.set([]);
		}
		cameras.uiData.setPosition(-50, -100);
		cameras.flags.display.set(true);
		cameras.params.postCreateSpareParams();
		cameras.params.runOnSceneLoadHooks();
		return {node: cameras, children: cameras_nodes};
	}
	function create_text(parentNode: RootManagerNode) {
		var text = parentNode.createNode('geo');
		text.setName('text');
		const text_nodes: ReturnedNodeDataDict = {};
		function create_text1(text: GeoObjNode) {
			var text1 = text.createNode('text');
			text1.setName('text1');
			const text1_nodes: ReturnedNodeDataDict = {};
			text1.uiData.setPosition(100, -150);
			text1.p.text.set('PULL & MOVE AROUND');
			text1.p.type.set(1);
			text1.p.size.set(0.42);
			text1.p.justifyMode.set(2);
			text1.params.postCreateSpareParams();
			text1.params.runOnSceneLoadHooks();
			return {node: text1, children: text1_nodes};
		}
		function create_transform1(text: GeoObjNode) {
			var transform1 = text.createNode('transform');
			transform1.setName('transform1');
			const transform1_nodes: ReturnedNodeDataDict = {};
			transform1.uiData.setPosition(100, 0);
			transform1.p.t.set([0, 0.8137011820950761, -3]);
			transform1.params.postCreateSpareParams();
			transform1.params.runOnSceneLoadHooks();
			return {node: transform1, children: transform1_nodes};
		}
		function create_material1(text: GeoObjNode) {
			var material1 = text.createNode('material');
			material1.setName('material1');
			const material1_nodes: ReturnedNodeDataDict = {};
			material1.uiData.setPosition(100, 150);
			material1.flags.display.set(true);
			material1.p.material.set('../MAT/meshBasic1');
			material1.params.postCreateSpareParams();
			material1.params.runOnSceneLoadHooks();
			return {node: material1, children: material1_nodes};
		}
		function create_MAT(text: GeoObjNode) {
			var MAT = text.createNode('materialsNetwork');
			MAT.setName('MAT');
			const MAT_nodes: ReturnedNodeDataDict = {};
			function create_meshBasic1(MAT: MaterialsNetworkSopNode) {
				var meshBasic1 = MAT.createNode('meshBasic');
				meshBasic1.setName('meshBasic1');
				const meshBasic1_nodes: ReturnedNodeDataDict = {};
				meshBasic1.uiData.setPosition(-150, 0);
				meshBasic1.params.postCreateSpareParams();
				meshBasic1.params.runOnSceneLoadHooks();
				return {node: meshBasic1, children: meshBasic1_nodes};
			}
			MAT_nodes['meshBasic1'] = create_meshBasic1(MAT);
			if (MAT.childrenController) {
				MAT.childrenController.selection.set([]);
			}
			MAT.uiData.setPosition(-100, 150);
			MAT.params.postCreateSpareParams();
			MAT.params.runOnSceneLoadHooks();
			return {node: MAT, children: MAT_nodes};
		}
		text_nodes['text1'] = create_text1(text);
		text_nodes['transform1'] = create_transform1(text);
		text_nodes['material1'] = create_material1(text);
		text_nodes['MAT'] = create_MAT(text);
		text_nodes['transform1'].node.setInput(0, text_nodes['text1'].node);
		text_nodes['material1'].node.setInput(0, text_nodes['transform1'].node);
		if (text.childrenController) {
			text.childrenController.selection.set([]);
		}
		text.uiData.setPosition(150, -300);
		text.flags.display.set(true);
		text.params.postCreateSpareParams();
		text.params.runOnSceneLoadHooks();
		return {node: text, children: text_nodes};
	}
	scene_root_nodes['geo1'] = create_geo1(root);
	scene_root_nodes['ground'] = create_ground(root);
	scene_root_nodes['lights'] = create_lights(root);
	scene_root_nodes['cameras'] = create_cameras(root);
	scene_root_nodes['text'] = create_text(root);
	scene.setFrame(0);
	scene.play();
	return {
		scene,
		cameraPath: '/cameras/cameras:sopGroup/perspectiveCamera_MAIN',
		focusedNodes: scene_root_nodes['geo1'].node.children(),
		autoLayout: false,
		playWhenLoaded: true,
	};
}
